import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { find, has, get } from 'lodash';
import {
  QUESTION_MULTIPLE,
  QUESTION_MATRIX_MAXDIFF,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
  GROUP_TYPE_CONJOINT,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
} from '../../constants';
import { escapeHTML, doesAnswersContainsOnlyEmoji } from '../../utils';
import AnswerWithImage from './AnswerWithImage';
import AttributeValues from './AttributeValues';
import { shuffleAnswers, getAnswerClass } from './utils';
import useComponentWillMount from '../hooks/useComponentWillMount';

export default function Answers({ question }) {
  const questionType = question.style || question.type;
  const shouldRender =
    questionType !== QUESTION_MATRIX_MAXDIFF &&
    questionType !== QUESTION_MATRIX_BIPOLAR &&
    questionType !== QUESTION_DRILLDOWN &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED_NUMERIC &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM;
  const containsOnlyEmoji = useMemo(
    () => doesAnswersContainsOnlyEmoji(get(question, 'answers', [])),
    [question],
  );

  const handleTap = useCallback(
    (answer) => {
      let answerEl = $(answer);

      if (!answerEl.hasClass('answer')) {
        answerEl = answerEl.closest('div.answer');
      }
      const otherContainer = $('#otherContainer');

      // we can select only one exclusive answer per question
      // this feature is allowed only in multiple select
      if (answerEl.hasClass('exclusive-answer')) {
        // unselect selected answers
        answerEl.siblings().removeClass('answerClick');
        otherContainer.hide();
      } else if (answerEl.hasClass('answer--multi-choice')) {
        $('.exclusive-answer').removeClass('answerClick');
        // if we select another answer and other answer is exclusive
        // we should hide the other input
        if ($('.answer-other').hasClass('exclusive-answer')) {
          otherContainer.hide();
        }
      }

      // if the question is not multi choice then remove
      // the previously selected answer
      if (
        !answerEl.hasClass('answer--multi-choice') ||
        Pollfish.event.clickedNoneOfTheAbove(answerEl) ||
        Pollfish.event.noneOfTheAboveSelected()
      ) {
        // unselect selected answers
        $('.answerClick').removeClass('answerClick');
        if (!Pollfish.event.clickedOtherAnswer(answerEl)) {
          otherContainer.hide();
        }

        if (
          !$('[data-btn="submit-btn"]').isOnScreen() &&
          Pollfish.helper.flag.showScrollDownMsg
        ) {
          Pollfish.helper.flag.showScrollDownMsg = false;
          Pollfish.ui.prompt(Pollfish.ui.getText('scroll_down'));
        }
      }

      answerEl.toggleClass('answerClick');

      if (Pollfish.event.clickedOtherAnswer(answerEl)) {
        const otherContainerCallback = () => {
          if (otherContainer.is(':visible')) {
            const wrapper = $('html');
            wrapper.animate({
              scrollTop: otherContainer.offset().top + wrapper.scrollTop(),
            });
          }
        };

        if (questionType === QUESTION_MULTIPLE) {
          otherContainer.toggle(otherContainerCallback);
        } else {
          otherContainer.show(otherContainerCallback);
        }
      }

      if (
        answerEl.hasClass('answer--multi-choice') &&
        otherContainer.is(':visible')
      ) {
        $('#otherContainerInput').blur();
      }
    },
    [questionType],
  );

  // jquery attaches the listener after the first render if we add it inside the
  // use effect call. So we need to call the initialiser before the first render
  // the UI is shown before the listers are added (and some cypress tests break)
  useComponentWillMount(() => {
    // we need to be careful to not add the answer event listeners to the question styles
    // that do not support it
    if (shouldRender) {
      $('#wrapper').on('tap', '.answer', (e) => {
        e.preventDefault();
        handleTap(e.target);
      });
    }
  });

  useEffect(() => {
    if (!shouldRender) {
      return () => {};
    }

    return () => {
      $('#wrapper').off('tap', '.answer');
    };
  }, [handleTap, shouldRender]);

  // if group type is max-diff do not proceed with render
  // since it will be handled by the specific question type
  if (!shouldRender) return null;

  let answerTemplate;
  const answersTemplate = [];
  const answers = shuffleAnswers(question);
  // we keep those to handle straightlining
  Pollfish.store.shuffledAnswers = answers;

  let isConjoint = false;
  if (has(question, 'groupRefId')) {
    const group = find(Pollfish.survey.groups, ['refId', question.groupRefId]);
    if (group.type === GROUP_TYPE_CONJOINT) {
      isConjoint = true;
      const { currentChoiceSet } = group;
      if (!currentChoiceSet) {
        throw new Error('Missing a current concept set for group');
      }
      currentChoiceSet.conceptAnswers.forEach((conceptAnswer) => {
        answersTemplate.push(
          <div
            id={conceptAnswer.id}
            key={conceptAnswer.id}
            className={`answer answer--conjoint ${getAnswerClass({
              question,
              containsOnlyEmoji,
            })} text-center`}
          >
            <AttributeValues values={conceptAnswer.attributeValues} />
          </div>,
        );
      });

      // TODO: this needs handling for when we fix conjoint validations
      Pollfish.store.shuffledAnswers = [
        ...currentChoiceSet.conceptAnswers,
        ...Pollfish.store.shuffledAnswers,
      ];
    }
  }
  answers.map((answer) => {
    // if group type is conjoint do nothing and answer type is not none of the above
    if (isConjoint && answer.style !== ANSWER_NONE_OF_THE_ABOVE) {
      return true;
    }
    // if group type is conjoint and answer type non of the above
    if (isConjoint && answer.style === ANSWER_NONE_OF_THE_ABOVE) {
      answersTemplate.push(
        <div
          id={answer.id}
          key={`hidden-answer-${answer.id}`}
          className={`answer answer--conjoint ${getAnswerClass({
            question,
            answer,
            containsOnlyEmoji,
          })} text-center`}
        >
          <div className="padding-md-horizontal padding-md-top padding-md-btm answer--conjoint-wrapper">
            <span className="answer--conjoint-color">
              {escapeHTML(answer.body) || '&nbsp;'}
            </span>
          </div>
        </div>,
      );
      return true;
    }
    const hasImageToShow = Boolean(
      answer.optimized_image_url || answer.image_url,
    );
    if (hasImageToShow) {
      answerTemplate = (
        <AnswerWithImage
          key={`answer-with-image-${answer.id}`}
          question={question}
          answer={answer}
          containsOnlyEmoji={containsOnlyEmoji}
        />
      );
    } else {
      answerTemplate = (
        <div
          id={answer.id}
          key={`hidden-answer-${answer.id}`}
          className={`answer ${getAnswerClass({
            question,
            answer,
            containsOnlyEmoji,
          })}`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: answer.body || '&nbsp;' }}
        ></div>
      );
    }

    answersTemplate.push(answerTemplate);

    if (answer.style === ANSWER_OTHER) {
      answersTemplate.push(
        <div id="otherContainer" key="other-container">
          <textarea
            id="otherContainerInput"
            className="input-text block"
            placeholder={escapeHTML(answer.label)}
            rows="1"
            dir="auto"
          ></textarea>
        </div>,
      );
    }
    return true;
  });

  return <div className="answers">{answersTemplate}</div>;
}

Answers.propTypes = {
  question: PropTypes.object,
};
